import type {
    LoadState,
    SaveState,
    SaveWorkspaceParams,
    WorkspaceLoadOptions,
    WorkspaceService,
    WorkspaceSettingsUpdate,
} from '.';
import { WorkspaceNotFoundError, WorkspacePermissionError, WorkspaceSaveInProgressError } from './errors';
import { getWorkspaceViewModelSetup } from './getWorkspaceViewModelSetup';
import type { LinkEvent, LinkEventHandler, LinkIntent, PublishLinkIntentOptions, WidgetLinkSource } from './linking';
import { WorkspaceOrchestrationModel } from './orchestrationModel';
import type { AnySelfOwnedWidgetClass, WidgetContainerId, LedgerEntry, WidgetId, WindowId } from './selfOwnedWidget';
import {
    decodeSelfOwnedWidgetState,
    encodeSelfOwnedWidgetState,
    getSelfOwnedWidgetInitialStateSchema,
    widgetContainerIdSchema,
    windowIdSchema,
} from './selfOwnedWidget';
import type { WidgetContainer as LiveWidgetContainer } from './WidgetContainer/WidgetContainer';
import type { WidgetContainerHost } from './WidgetContainerHost';
import { WEB_WINDOW_ID } from './windowIds';
import { Signal, effect } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { MutationObserver } from '@tanstack/query-core';
import { doesAccessGrantAllowRequest } from '@thinkalpha/common/util/permissions.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { generateSlug } from '@thinkalpha/ui-core/slug';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type {
    Layout,
    NewWorkspace,
    UpdatedWorkspace,
    WidgetContainer as PersistedWidgetContainer,
    WidgetCreationInfo,
    WidgetTab,
    Workspace as LocalContractsWorkspace,
    WidgetType,
} from '@thinkalpha/ui-types/contracts/workspace';
import { isEqual } from 'es-toolkit';
import { t } from 'i18next';
import { optional } from 'inversify';
import { inject, injectable, skipAutoDispose } from 'src/features/ioc';
import {
    channelColors,
    externalAppLinkingChannelId,
    externalAppLinkingLabel,
} from 'src/features/widget/components/WidgetHeader/WidgetHeaderMenus/WidgetHeaderMenuChannelPicker';
import { parseInstrument } from 'src/lib/instrumentUtils';
import { isRoutePath } from 'src/lib/navigation/path';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { createWorkspaceFromContainers, generateWidgetTabViewModels, stripUnrecognizedWidgets } from 'src/lib/widget';
import { getWidgetFromCreationInfo } from 'src/lib/widget/widgetCreation';
import { CASCADE_DIMENSIONS, DEFAULT_DIMENSIONS, computeWidgetLayout } from 'src/lib/widget/widgetLayout';
import type { WidgetLayoutResult } from 'src/lib/widget/widgetLayout';
import { allWidgets, getWidgetDefinition, getWidgetDisplayName } from 'src/lib/widget/widgets';
import type { RegisteredWidgetType } from 'src/lib/widget/widgets';
// TODO refactor to use a query/mutation from src/queries that can depend on this API call
// eslint-disable-next-line no-restricted-imports -- legacy direct import
import { setDefaultWorkspaceId as changeDefaultWorkspaceOnAPI } from 'src/queries/http/userPreference';
import {
    getCreateWorkspaceMutation,
    getUpdateWorkspaceMutation,
    prefetchWorkspaceLoadDependencies,
} from 'src/queries/workspace';
import { ceilToMosaicGrid } from 'src/routes/dashboard/svelte-layout/mosaic-drop-targets';
import type { NormalizedLayout } from 'src/routes/dashboard/svelte-layout/mosaic-drop-targets';
import { getMosaicTemplateById } from 'src/routes/dashboard/svelte-layout/mosaic-templates';
import { growLayoutToFillGaps } from 'src/routes/dashboard/svelte-layout/mosaic-widget-placement';
import { scoreCommand } from 'src/services/CommandPaletteService/scoring';
import type {
    CommandAction,
    CommandEntry,
    CommandHandler,
    CommandQueryRequest,
    CommandQueryResponse,
} from 'src/services/CommandPaletteService/types';
import type { LocalWidgetContainerData } from 'src/services/LocalWidgetStorageService';
import type { NavigationService } from 'src/services/NavigationService';
import { container as staticContainer } from 'src/StaticContainer';
import type { RootState } from 'src/store';
import {
    addContainer,
    createAlphaEyeFromSymbol,
    createChartFromSymbol,
    createNewsFromSymbol,
    cycleActiveContainer,
    removeContainer as removeContainerAction,
    setActiveContainer,
    setMaximizedContainer,
} from 'src/store/actions/container';
import type { AddWidgetToDashboardAction } from 'src/store/actions/container';
import { updateMultipleWorkspaceLayouts } from 'src/store/actions/layout';
import type { LayoutUpdate } from 'src/store/actions/layout';
import { addTab, removeTab as removeTabAction, updateActiveTab } from 'src/store/actions/tab';
import type { AddNewWidgetToContainerAction } from 'src/store/actions/tab';
import { setSnackbar } from 'src/store/actions/ui';
import {
    workspaceLinkingAccountSelected,
    workspaceLinkingInstrumentSelected,
    workspaceLinkingOrderSelected,
    workspaceLinkingQuantitySelected,
} from 'src/store/actions/widgets/linking';
import {
    clearPendingTemplateConfig,
    clearWorkspaceState,
    completeWorkspaceResearch,
    initializeNormalizedData,
    initializeUnsavedWorkspace,
    markMosaicTemplateSlotOccupied,
    markWorkspaceDirty,
    savingCancelled,
    savingCompleted,
    setMosaicTemplate,
    setResearchingWorkspace,
    setSavingTrue,
    setWorkspaceError,
    updateWorkspaceSettings,
    workspaceSavingFailed,
} from 'src/store/actions/workspace';
import type { CreateFromTemplateLibraryAction } from 'src/store/actions/workspace';
import { UNSAVED_WORKSPACE } from 'src/store/reducers/workspace';
import { getMaximizedContainerId } from 'src/store/selectors/container';
import { getFirstTabByWidgetType, getWidgetCountByType } from 'src/store/selectors/tab';
import {
    getAssertedCurrentWorkspace,
    getCurrentlyVisibleWorkspaceId,
    getCurrentWorkspace,
} from 'src/store/selectors/workspace';
import type {
    ContainerLayoutViewModel,
    ContainerViewModel,
    WidgetTabViewModel,
    WorkspaceViewModel,
} from 'src/store/types';
import { createContainerLayoutViewModel, createContainerViewModel } from 'src/store/types/workspaceViewModels';
import type { IsomorphicBindings, ReactBindings } from 'src/types/bindings';
import { v4 as uuidv4 } from 'uuid';

const OVERLAY_DELAY_MS = 175;

const stringifyForDebug = (value: unknown): string =>
    JSON.stringify(value, (_key, nestedValue) => (nestedValue === undefined ? '<undefined>' : nestedValue));

class WorkspaceLoadAttempt implements Disposable {
    readonly #abortController = new AbortController();
    #closeReason: 'workspace-replaced' | 'service-disposed' = 'workspace-replaced';

    constructor(readonly generation: number) {}

    get closeReason(): 'workspace-replaced' | 'service-disposed' {
        return this.#closeReason;
    }

    get signal(): AbortSignal {
        return this.#abortController.signal;
    }

    cancel(reason: 'workspace-replaced' | 'service-disposed'): void {
        if (this.#abortController.signal.aborted) return;
        this.#closeReason = reason;
        this.#abortController.abort();
    }

    [Symbol.dispose](): void {
        this.cancel('workspace-replaced');
    }
}

function isLocalOnlyWidgetType(type: WidgetType): boolean {
    if (type === 'alpha-lens') return false;
    return allWidgets[type]?.isLocalOnly === true;
}

function partitionContainersByLocality(containers: PersistedWidgetContainer[]): {
    localContainers: LocalWidgetContainerData[];
    remoteContainers: PersistedWidgetContainer[];
} {
    const localContainers: LocalWidgetContainerData[] = [];
    const remoteContainers: PersistedWidgetContainer[] = [];

    for (const container of containers) {
        const localTabs = container.tabs.filter((tab) => isLocalOnlyWidgetType(tab.widget.type));
        const remoteTabs = container.tabs.filter((tab) => !isLocalOnlyWidgetType(tab.widget.type));

        if (localTabs.length > 0) {
            localContainers.push({
                containerId: container.id,
                tabs: localTabs,
                layout: container.layout,
                activeTabId:
                    container.activeTabId !== null && localTabs.some((tab) => tab.id === container.activeTabId)
                        ? container.activeTabId
                        : null,
            });
        }

        if (remoteTabs.length > 0) {
            const remoteTabIds = new Set(remoteTabs.map((tab) => tab.id));
            const newActiveTabId =
                container.activeTabId !== null && remoteTabIds.has(container.activeTabId)
                    ? container.activeTabId
                    : remoteTabs[0].id;

            remoteContainers.push({
                id: container.id,
                tabs: remoteTabs,
                layout: container.layout,
                activeTabId: newActiveTabId,
            });
        }
    }

    return { localContainers, remoteContainers };
}

function createInitialSelfOwnedState(widgetClass: AnySelfOwnedWidgetClass, widget: object): unknown {
    const schema = getSelfOwnedWidgetInitialStateSchema(widgetClass);
    const defaultState = widgetClass.defaultInitialState?.() ?? widgetClass.defaultState();
    const defaultWireState = encodeSelfOwnedWidgetState(schema, defaultState);
    return decodeSelfOwnedWidgetState(schema, {
        ...(defaultWireState as Record<string, unknown>),
        ...(widget as Record<string, unknown>),
    });
}

function encodeInitialSelfOwnedState(widgetClass: AnySelfOwnedWidgetClass, state: unknown): unknown {
    return encodeSelfOwnedWidgetState(getSelfOwnedWidgetInitialStateSchema(widgetClass), state);
}

function encodeLedgerSelfOwnedState(widgetClass: AnySelfOwnedWidgetClass, state: unknown): unknown {
    try {
        return encodeInitialSelfOwnedState(widgetClass, state);
    } catch {
        // Invalid saved blobs are deliberately retained in the ledger so an
        // ErrorWidgetModel can hold and round-trip them without data loss.
        return state;
    }
}

@injectable()
export class WorkspaceServiceImpl implements WorkspaceService, Disposable {
    readonly [skipAutoDispose] = true;

    // eslint-disable-next-line @thinkalpha/eslint-rules/no-typescript-private-properties -- required pattern
    private subscription: () => void;
    // eslint-disable-next-line @thinkalpha/eslint-rules/no-typescript-private-properties -- required pattern
    private readonly containerLedgerSubscription: () => void;

    readonly #log: Logger;
    readonly #createWorkspaceMutation: MutationObserver<LocalContractsWorkspace, Error, { workspace: NewWorkspace }>;
    readonly #updateWorkspaceMutation: MutationObserver<
        LocalContractsWorkspace,
        Error,
        { workspace: UpdatedWorkspace }
    >;
    readonly #orchestration: WorkspaceOrchestrationModel;
    readonly #activeLoadAttempt = new DisposableBox<WorkspaceLoadAttempt>();

    readonly #containers = new Map<WidgetContainerId, LiveWidgetContainer>();
    readonly #containerDirtySubscriptions = new Map<WidgetContainerId, () => void>();

    readonly #ledger = new Map<WidgetId, LedgerEntry<unknown>>();
    readonly #widgetRegistry = new Map<string, object>();
    readonly #linkEventHandlers = new Set<LinkEventHandler>();

    constructor(
        @inject('Store') private readonly store: IsomorphicBindings['Store'],
        @inject('QueryClient') private readonly queryClient: IsomorphicBindings['QueryClient'],
        @inject('Logger') logger: Logger,
        @inject('LocalWidgetStorageService')
        private readonly localWidgetStorageService: IsomorphicBindings['LocalWidgetStorageService'],
        @inject('SuggestionsService') private readonly suggestionsService: IsomorphicBindings['SuggestionsService'],
        @inject('WidgetContainerFactory')
        @optional()
        private readonly widgetContainerFactory: ReactBindings['WidgetContainerFactory'] | undefined,
        @inject('TProvider')
        private readonly tProvider: IsomorphicBindings['TProvider'],
        @inject('SelfOwnedWidgetRegistry')
        private readonly selfOwnedWidgetRegistry: IsomorphicBindings['SelfOwnedWidgetRegistry'],
        @inject('WidgetContainerHost')
        @optional()
        private readonly widgetContainerHost?: WidgetContainerHost,
        @inject('NavigationService')
        @optional()
        private readonly navigationService?: NavigationService,
    ) {
        this.#log = logger.getSubLogger({ name: 'WorkspaceService' });
        this.#createWorkspaceMutation = new MutationObserver(this.queryClient, getCreateWorkspaceMutation());
        this.#updateWorkspaceMutation = new MutationObserver(this.queryClient, getUpdateWorkspaceMutation());
        this.#orchestration = new WorkspaceOrchestrationModel({
            createWorkspaceMutation: this.#createWorkspaceMutation,
            updateWorkspaceMutation: this.#updateWorkspaceMutation,
        });

        this.subscription = this.store
            .subscribeSelector(getCurrentlyVisibleWorkspaceId, { leading: true })
            .handle((id) => (this.#id.value = id));

        this.containerLedgerSubscription = this.widgetContainerHost
            ? () => undefined
            : this.#startContainerLedgerSync();
    }

    #routeLocation() {
        return this.navigationService?.location.value;
    }

    async #navigate(to: string): Promise<void> {
        await this.navigationService?.navigate(to);
    }

    #startContainerLedgerSync(): () => void {
        return this.store
            .subscribeSelector((state: RootState) => state.container.byId, { leading: true })
            .handle((byId) => {
                const presentIds = new Set<string>(Object.keys(byId));

                for (const [id, container] of this.#containers) {
                    if (!presentIds.has(id)) {
                        this.#containerDirtySubscriptions.get(id)?.();
                        this.#containerDirtySubscriptions.delete(id);
                        this.#containers.delete(id);
                        void container.dispose();
                    }
                }

                for (const id of presentIds) {
                    if (this.#containers.has(id as WidgetContainerId)) continue;
                    const layoutVm = this.store.getState().layout.byContainerId[id] as
                        | ContainerLayoutViewModel
                        | undefined;
                    if (!layoutVm) {
                        // Layout hasn't been dispatched yet — skip; the next state update will retry.
                        continue;
                    }
                    const layout: Layout = {
                        x: layoutVm.x,
                        y: layoutVm.y,
                        w: layoutVm.w,
                        h: layoutVm.h,
                        ...(layoutVm.screen !== undefined ? { screen: layoutVm.screen } : {}),
                    };
                    const container = this.#getWidgetContainerFactory()();
                    const containerId = widgetContainerIdSchema.parse(id);
                    this.#containers.set(containerId, container);
                    void container
                        .init({
                            id: containerId,
                            windowId: WEB_WINDOW_ID,
                            layout,
                        })
                        .then(
                            () => {
                                if (this.#containers.get(containerId) !== container) return;
                                this.#subscribeToContainerDirty(containerId, container);
                            },
                            (error: unknown) => {
                                if (error instanceof Error) {
                                    this.#log.error({ message: 'Container initialization failed', error });
                                } else {
                                    this.#log.fatal({ message: 'unknown exception', details: error });
                                }
                            },
                        );
                }
            });
    }

    #getWidgetContainerFactory(): ReactBindings['WidgetContainerFactory'] {
        if (!this.widgetContainerFactory) {
            throw new Error('WorkspaceService requires WidgetContainerFactory when no WidgetContainerHost is bound');
        }
        return this.widgetContainerFactory;
    }

    #subscribeToContainerDirty(containerId: WidgetContainerId, container: LiveWidgetContainer): void {
        if (this.#containerDirtySubscriptions.has(containerId)) return;

        let wasDirty = false;
        const dispose = effect(() => {
            const isDirty = container.dirty.value;
            if (isDirty && !wasDirty) {
                void this.setContainerDirty(containerId, true);
            }
            wasDirty = isDirty;
        });
        this.#containerDirtySubscriptions.set(containerId, dispose);
    }

    getWidgetContainer(containerId: string): LiveWidgetContainer | undefined {
        return this.#containers.get(containerId as WidgetContainerId);
    }

    async setContainerDirty(containerId: string, dirty: boolean): Promise<void> {
        if (!dirty) return;

        const currentWorkspace = getCurrentWorkspace(this.store.getState());
        if (!currentWorkspace?.containerIds.includes(containerId)) return;

        this.store.dispatch(markWorkspaceDirty());
    }

    async removeWidget(tabId: string): Promise<void> {
        const state = this.store.getState() as RootState;
        const tab = state.tab.byId[tabId];
        if (!tab) return;

        const containerId = tab.containerId;
        const containerVm = state.container.byId[containerId];
        if (!containerVm) return;

        if (containerVm.tabIds.length <= 1) {
            await this.removeContainer(containerId);
            return;
        }

        const container = this.#containers.get(containerId as WidgetContainerId);
        await container?.disposeWidget(tabId as WidgetId);
        this.#ledger.delete(tabId as WidgetId);

        const newActiveTabId = containerVm.tabIds.find((id) => id !== tabId);
        const widgetType = tab.widget?.type ?? 'unknown';
        this.store.dispatch(removeTabAction(tabId, containerId, newActiveTabId!, widgetType));
    }

    async removeContainer(containerId: string): Promise<void> {
        const state = this.store.getState() as RootState;
        const containerVm = state.container.byId[containerId];

        if (containerVm) {
            for (const tabId of containerVm.tabIds) {
                this.#ledger.delete(tabId as WidgetId);
            }
        }

        this.#containerDirtySubscriptions.get(containerId as WidgetContainerId)?.();
        this.#containerDirtySubscriptions.delete(containerId as WidgetContainerId);
        const container = this.#containers.get(containerId as WidgetContainerId);
        this.#containers.delete(containerId as WidgetContainerId);

        if (this.widgetContainerHost) {
            await this.widgetContainerHost.closeContainer(containerId as WidgetContainerId, 'container-removed');
        } else {
            await container?.dispose();
        }

        if (containerVm) {
            this.store.dispatch(removeContainerAction(containerId));
        }
    }

    async closeWidgetContainers(): Promise<void> {
        this.#cancelActiveLoad();
        await this.#closeTrackedContainers('service-disposed');
    }

    #beginLoad(): WorkspaceLoadAttempt {
        const attempt = new WorkspaceLoadAttempt(this.#orchestration.beginLoad());
        this.#activeLoadAttempt.next(attempt);
        return attempt;
    }

    #cancelActiveLoad(): void {
        const attempt = this.#activeLoadAttempt.current;
        if (!attempt) return;

        // Advance the generation before aborting so every continuation observes
        // the attempt as stale, including abort listeners which run synchronously.
        this.#orchestration.cancelLoad(attempt.generation);
        attempt.cancel('service-disposed');
        this.#activeLoadAttempt.clear();
    }

    #endLoad(attempt: WorkspaceLoadAttempt): void {
        if (this.#activeLoadAttempt.current === attempt) {
            // A completed attempt no longer needs cancellation; detach it without
            // invoking its abort disposal.
            this.#activeLoadAttempt.release();
        }
        this.#orchestration.endLoad(attempt.generation);
    }

    #isStaleLoad(attempt: WorkspaceLoadAttempt): boolean {
        return attempt.signal.aborted || this.#orchestration.isStaleLoad(attempt.generation);
    }

    async handleAddWidgetAction(action: AddWidgetToDashboardAction): Promise<void> {
        await this.#orchestrateAddWidget(action.widget, action.preferredDimensions);
    }

    async handleAddNewWidgetToContainerAction(action: AddNewWidgetToContainerAction): Promise<void> {
        await this.#addNewWidgetToContainer(action.tab, action.containerId);
    }

    async #orchestrateAddWidget(
        creationInfo: WidgetCreationInfo,
        preferredDimensions?: { w: number; h: number },
    ): Promise<void> {
        const stateAtStart = this.store.getState() as RootState;

        const currentlyMaximizedContainer = getMaximizedContainerId(stateAtStart);
        if (currentlyMaximizedContainer) {
            await this.#addNewWidgetToContainer(creationInfo, currentlyMaximizedContainer);
            return;
        }

        const initialWidget = await getWidgetFromCreationInfo(creationInfo, stateAtStart, this.queryClient);
        const widgetType = initialWidget.type;
        const widgetDefinition = getWidgetDefinition(widgetType as RegisteredWidgetType);

        if (widgetDefinition.maxInstances !== undefined) {
            const refreshedState = this.store.getState() as RootState;
            const currentCount = getWidgetCountByType(refreshedState, widgetType);
            if (currentCount >= widgetDefinition.maxInstances) {
                if (TRACE) {
                    this.#log.trace('Widget instance limit reached', { widgetType });
                }
                const existingTab = getFirstTabByWidgetType(refreshedState, widgetType);
                if (existingTab) {
                    this.store.dispatch(updateActiveTab(existingTab.containerId, existingTab.id));
                    this.store.dispatch(setActiveContainer(existingTab.containerId));
                }
                return;
            }
        }

        let creationInfoFinal: WidgetCreationInfo = creationInfo;
        if (
            creationInfoFinal.creationType === 'from-widget-default' &&
            !creationInfoFinal.targetRect &&
            !creationInfoFinal.templateSlotId
        ) {
            const workspace = stateAtStart.workspace.currentWorkspace;
            const template = workspace?.mosaicTemplate;
            if (template) {
                const availableSlot = template.slots.find((slot) => !template.occupiedSlotIds.includes(slot.id));
                if (availableSlot) {
                    creationInfoFinal = {
                        ...creationInfoFinal,
                        targetRect: availableSlot.rect,
                        fillTargetRect: true,
                        templateSlotId: availableSlot.id,
                    };
                }
            }
        }

        let layoutResult: WidgetLayoutResult = { layout: { ...DEFAULT_DIMENSIONS } };
        const stateForLayout = this.store.getState() as RootState;
        switch (creationInfoFinal.creationType) {
            case 'from-widget-default': {
                const def = getWidgetDefinition(initialWidget.type as RegisteredWidgetType);
                if (creationInfoFinal.targetRect) {
                    const rect = creationInfoFinal.targetRect;
                    if (creationInfoFinal.fillTargetRect) {
                        layoutResult = { layout: { x: rect.x, y: rect.y, w: rect.w, h: rect.h } };
                    } else {
                        const preferredW = Math.min(
                            ceilToMosaicGrid(
                                preferredDimensions?.w ?? def.defaultDimensions?.w ?? DEFAULT_DIMENSIONS.w,
                            ),
                            rect.w,
                        );
                        const preferredH = Math.min(
                            ceilToMosaicGrid(
                                preferredDimensions?.h ?? def.defaultDimensions?.h ?? DEFAULT_DIMENSIONS.h,
                            ),
                            rect.h,
                        );
                        const placedLayout: NormalizedLayout = {
                            x: rect.x,
                            y: rect.y,
                            w: preferredW,
                            h: preferredH,
                        };
                        const allLayouts = stateForLayout.layout.byContainerId;
                        const otherLayouts: NormalizedLayout[] = Object.values(allLayouts ?? {})
                            .filter(Boolean)
                            .map((l) => ({ x: l.x, y: l.y, w: l.w, h: l.h }));
                        const grownLayout = growLayoutToFillGaps(placedLayout, otherLayouts);
                        layoutResult = { layout: grownLayout };
                    }
                } else {
                    layoutResult = computeWidgetLayout(def, stateForLayout, undefined, preferredDimensions);
                    if (creationInfoFinal.workspaceLocation) {
                        const { x: clickX, y: clickY } = creationInfoFinal.workspaceLocation;
                        const { w, h } = layoutResult.layout;
                        layoutResult = {
                            ...layoutResult,
                            layout: {
                                ...layoutResult.layout,
                                x: Math.min(clickX, 1 - w),
                                y: Math.min(clickY, 1 - h),
                            },
                        };
                    }
                }
                break;
            }
            case 'new-alpha-lens':
            case 'new-alpha-eye':
                layoutResult = computeWidgetLayout(
                    getWidgetDefinition('alpha-eye'),
                    stateForLayout,
                    undefined,
                    preferredDimensions,
                );
                break;
            case 'new-tradingview-chart':
                layoutResult = computeWidgetLayout(
                    getWidgetDefinition('tradingview-chart'),
                    stateForLayout,
                    undefined,
                    preferredDimensions,
                );
                break;
            case 'new-searchalpha':
                layoutResult = computeWidgetLayout(
                    getWidgetDefinition('searchalpha'),
                    stateForLayout,
                    undefined,
                    preferredDimensions,
                );
                break;
            case 'new-strategy':
                layoutResult = computeWidgetLayout(
                    getWidgetDefinition('results'),
                    stateForLayout,
                    undefined,
                    preferredDimensions,
                );
                break;
            case 'new-news':
                layoutResult = computeWidgetLayout(
                    getWidgetDefinition('news'),
                    stateForLayout,
                    undefined,
                    preferredDimensions,
                );
                break;
            case 'new-filings':
                layoutResult = computeWidgetLayout(
                    getWidgetDefinition('filings'),
                    stateForLayout,
                    undefined,
                    preferredDimensions,
                );
                break;
            case 'new-events':
                layoutResult = computeWidgetLayout(
                    getWidgetDefinition('events'),
                    stateForLayout,
                    undefined,
                    preferredDimensions,
                );
                break;
            case 'cloned': {
                const clonedLayout = stateForLayout.layout.byContainerId[creationInfoFinal.baseWidgetContainerId];
                if (clonedLayout) {
                    const { x, y, ...rest } = clonedLayout;
                    layoutResult = {
                        layout: {
                            x: x + CASCADE_DIMENSIONS.x,
                            y: y + CASCADE_DIMENSIONS.y,
                            ...rest,
                        },
                    };
                }
                break;
            }
        }

        if (layoutResult.affectedLayouts) {
            const updates: LayoutUpdate[] = Object.entries(layoutResult.affectedLayouts).map(
                ([containerId, layout]) => ({ containerId, layout }),
            );
            this.store.dispatch(updateMultipleWorkspaceLayouts(updates));
        }

        const t = await this.tProvider();
        const widgetDisplayName = getWidgetDisplayName(t, initialWidget);
        const tabId = uuidv4() as WidgetId;
        const containerId = uuidv4();
        const widgetClass = this.selfOwnedWidgetRegistry.get(widgetType);
        const initialPersistedState = widgetClass ? createInitialSelfOwnedState(widgetClass, initialWidget) : undefined;

        const containerViewModel: ContainerViewModel = {
            id: containerId,
            activeTabId: tabId,
            tabIds: [tabId],
        };
        const layoutViewModel = createContainerLayoutViewModel(layoutResult.layout, containerId);
        const reduxWidget = widgetClass
            ? ({ type: widgetType, channelId: initialWidget.channelId || null } as WidgetTabViewModel['widget'])
            : initialWidget;
        const initialTabViewModel: WidgetTabViewModel = {
            id: tabId,
            name: widgetDisplayName,
            widget: reduxWidget,
            isActive: true,
            containerId,
            channelId: initialWidget.channelId || null,
        };

        // The ledger entry must exist before addContainer triggers container sync.
        const windowId: WindowId = this.widgetContainerHost ? windowIdSchema.parse(containerId) : WEB_WINDOW_ID;

        if (widgetClass) {
            this.#ledger.set(tabId, {
                id: tabId,
                type: widgetType,
                location: { type: 'workspace', tabId },
                windowId,
                persistedState: initialPersistedState,
            });
        }

        this.store.dispatch(addContainer(containerViewModel, initialTabViewModel, layoutViewModel));

        if (this.widgetContainerHost) {
            const contractWidget = widgetClass
                ? ({
                      ...reduxWidget,
                      ...(encodeInitialSelfOwnedState(widgetClass, initialPersistedState) as Record<string, unknown>),
                  } as WidgetTab['widget'])
                : (initialWidget as WidgetTab['widget']);
            const windowInitialJSON: PersistedWidgetContainer = {
                id: containerId,
                layout: layoutResult.layout,
                activeTabId: tabId,
                tabs: [{ id: tabId, name: widgetDisplayName, widget: contractWidget }],
            };
            const container = await this.widgetContainerHost.openContainer({
                id: containerId as WidgetContainerId,
                windowId,
                layout: layoutResult.layout,
                initialJSON: windowInitialJSON,
                refreshInitialJSON: () => this.#overlayLedgerState(windowInitialJSON),
            });
            this.#containers.set(containerId as WidgetContainerId, container);
        } else if (widgetClass) {
            const container = this.#containers.get(containerId as WidgetContainerId);
            if (container) {
                await container.addInitialWidget(
                    widgetType,
                    tabId,
                    encodeInitialSelfOwnedState(widgetClass, initialPersistedState),
                );
            }
        }

        if (creationInfoFinal.creationType === 'from-widget-default' && creationInfoFinal.templateSlotId) {
            this.store.dispatch(markMosaicTemplateSlotOccupied(creationInfoFinal.templateSlotId, containerId));
        }

        try {
            const analyticsService = staticContainer.getOptional('AnalyticsService');
            const currentWorkspace = (this.store.getState() as RootState).workspace.currentWorkspace;
            analyticsService?.track({
                type: 'widget_added',
                dashboardId: currentWorkspace?.id || '',
                widgetType,
                widgetId: tabId,
            });
        } catch (analyticsError) {
            this.#log.warn('Failed to track widget_added', { error: analyticsError });
        }
    }

    #seedSelfOwnedLedgerFromBlob(containers: readonly PersistedWidgetContainer[]): void {
        const nextWorkspaceTabIds = new Set<WidgetId>();
        for (const container of containers) {
            for (const tab of container.tabs) {
                nextWorkspaceTabIds.add(tab.id as WidgetId);
            }
        }

        for (const [tabId, entry] of this.#ledger) {
            if (entry.location.type === 'workspace' && !nextWorkspaceTabIds.has(tabId)) {
                this.#ledger.delete(tabId);
            }
        }

        for (const container of containers) {
            for (const tab of container.tabs) {
                const widgetClass = this.selfOwnedWidgetRegistry.get(tab.widget.type);
                if (!widgetClass) continue;
                let persistedState: unknown;
                try {
                    persistedState = decodeSelfOwnedWidgetState(widgetClass.stateSchema, tab.widget);
                } catch (error) {
                    // A saved blob may carry state the schema rejects — the
                    // save path preserves an error stand-in's state verbatim.
                    // Seed the raw blob so the tab keeps round-tripping;
                    // instantiation registers an ErrorWidgetModel for it.
                    this.#log.warn(`Seeding ledger for widget '${tab.widget.type}' with raw state: decode failed`, {
                        tabId: tab.id,
                        widgetType: tab.widget.type,
                        error,
                    });
                    persistedState = tab.widget;
                }
                this.#ledger.set(tab.id as WidgetId, {
                    id: tab.id as WidgetId,
                    type: tab.widget.type,
                    location: { type: 'workspace', tabId: tab.id },
                    windowId: this.widgetContainerHost ? windowIdSchema.parse(container.id) : WEB_WINDOW_ID,
                    persistedState,
                });
            }
        }
    }

    async #instantiateSelfOwnedWidgetsAfterDispatch(containers: readonly PersistedWidgetContainer[]): Promise<void> {
        // Hosted (windowed) containers self-register their widgets from the
        // window-init pull when the renderer is ready. Pushing addInitialWidget
        // at a window that is still booting races its handler registration —
        // the invoke is lost or times out, and a late arrival would displace
        // the model the window already built. Pushes remain only for runtime
        // adds against live windows.
        if (this.widgetContainerHost) return;

        for (const containerBlob of containers) {
            const container = this.#containers.get(containerBlob.id as WidgetContainerId);
            if (!container) continue;
            for (const tab of containerBlob.tabs) {
                const widgetClass = this.selfOwnedWidgetRegistry.get(tab.widget.type);
                if (!widgetClass) continue;
                if (container.getWidget(tab.id as WidgetId)) continue;
                try {
                    await container.addInitialWidget(
                        tab.widget.type,
                        tab.id as WidgetId,
                        encodeLedgerSelfOwnedState(widgetClass, this.#ledger.get(tab.id as WidgetId)?.persistedState),
                    );
                } catch (error) {
                    // One widget's failure must not blank the rest of the
                    // workspace; the container registers an ErrorWidgetModel
                    // stand-in that the host renders per widget.
                    this.#log.error(
                        `Self-owned widget '${tab.widget.type}' failed to instantiate during workspace load`,
                        { containerId: containerBlob.id, tabId: tab.id, widgetType: tab.widget.type, error },
                    );
                }
            }
        }
    }

    async #closeTrackedContainers(reason: 'workspace-replaced' | 'service-disposed'): Promise<void> {
        for (const unsubscribeDirty of this.#containerDirtySubscriptions.values()) {
            unsubscribeDirty();
        }
        this.#containerDirtySubscriptions.clear();

        for (const [tabId, entry] of this.#ledger) {
            if (entry.location.type === 'workspace') {
                this.#ledger.delete(tabId);
            }
        }

        if (this.widgetContainerHost) {
            await this.widgetContainerHost.closeAllContainers(reason);
        } else {
            await Promise.all([...this.#containers.values()].map((container) => container.dispose()));
        }

        this.#containers.clear();
    }

    async #syncHostedContainersFromBlob(
        containers: readonly PersistedWidgetContainer[],
        attempt: WorkspaceLoadAttempt,
    ): Promise<void> {
        if (!this.widgetContainerHost || this.#isStaleLoad(attempt)) return;

        const nextContainers = new Map<WidgetContainerId, PersistedWidgetContainer>(
            containers.map((container): [WidgetContainerId, PersistedWidgetContainer] => [
                container.id as WidgetContainerId,
                container,
            ]),
        );
        for (const [id, container] of [...this.#containers]) {
            if (this.#isStaleLoad(attempt)) return;

            const nextContainer = nextContainers.get(id);
            if (!nextContainer) {
                await this.widgetContainerHost.closeContainer(id, 'workspace-replaced');
                this.#containers.delete(id);
                if (this.#isStaleLoad(attempt)) return;
                continue;
            }

            const matchesBlob = await this.#hostedContainerMatchesBlob(container, nextContainer);
            if (this.#isStaleLoad(attempt)) return;
            if (!matchesBlob) {
                await this.widgetContainerHost.closeContainer(id, 'workspace-replaced');
                this.#containers.delete(id);
                if (this.#isStaleLoad(attempt)) return;
            }
        }

        for (const containerBlob of containers) {
            if (this.#isStaleLoad(attempt)) return;

            const containerId = containerBlob.id as WidgetContainerId;
            if (this.#containers.has(containerId)) continue;
            let container: LiveWidgetContainer;
            try {
                container = await this.widgetContainerHost.openContainer({
                    id: containerId,
                    windowId: windowIdSchema.parse(containerBlob.id),
                    layout: containerBlob.layout,
                    initialJSON: containerBlob,
                    refreshInitialJSON: () => this.#overlayLedgerState(containerBlob),
                });
            } catch (error) {
                this.#log.error(`Unable to open hosted WidgetContainer '${containerId}' during workspace load`, {
                    containerId,
                    error,
                });
                if (this.#isStaleLoad(attempt)) return;
                continue;
            }
            if (this.#isStaleLoad(attempt)) {
                await this.widgetContainerHost.closeContainer(containerId, attempt.closeReason);
                return;
            }
            this.#containers.set(containerId, container);
        }
    }

    /**
     * Overlays each tab's current ledger persisted state onto the open-time
     * container snapshot. Answers native window-init pulls (including Ctrl+R
     * reloads) so a reloaded window rehydrates the ledger's latest widget
     * state. Deliberately reads only stable main-process sources — the frozen
     * snapshot for tab existence and the ledger for state — never redux,
     * which is mid-transition during workspace switches while windows boot
     * asynchronously.
     */
    #overlayLedgerState(containerJSON: PersistedWidgetContainer): PersistedWidgetContainer {
        return {
            ...containerJSON,
            tabs: containerJSON.tabs.map((tab) => {
                const persistedState = this.#ledger.get(tab.id as WidgetId)?.persistedState;
                if (persistedState === undefined) return tab;
                const widgetClass = this.selfOwnedWidgetRegistry.get(tab.widget.type);
                const wireState = widgetClass
                    ? encodeLedgerSelfOwnedState(widgetClass, persistedState)
                    : persistedState;
                return {
                    ...tab,
                    widget: { ...tab.widget, ...(wireState as Record<string, unknown>) } as WidgetTab['widget'],
                };
            }),
        };
    }

    async #hostedContainerMatchesBlob(
        container: LiveWidgetContainer,
        containerBlob: PersistedWidgetContainer,
    ): Promise<boolean> {
        try {
            const currentJSON = await container.toJSON();
            const layoutMatches = isEqual(currentJSON.layout, containerBlob.layout);
            const tabsMatch = isEqual(currentJSON.tabs, containerBlob.tabs);
            const activeTabMatches = currentJSON.activeTabId === (containerBlob.activeTabId ?? null);
            const matches = layoutMatches && tabsMatch && activeTabMatches;

            if (!matches) {
                const mismatchedFields = [
                    ...(!layoutMatches ? ['layout'] : []),
                    ...(!tabsMatch ? ['tabs'] : []),
                    ...(!activeTabMatches ? ['activeTabId'] : []),
                ];

                this.#log.debug(
                    `Recreating hosted WidgetContainer '${containerBlob.id}': ${mismatchedFields.join(', ')} changed`,
                    {
                        containerId: containerBlob.id,
                        mismatchedFields,
                        liveContainerJSON: stringifyForDebug(currentJSON),
                        fetchedContainerJSON: stringifyForDebug(containerBlob),
                    },
                );
            }

            return matches;
        } catch (error) {
            this.#log.warn('Unable to serialize hosted container before workspace sync', {
                containerId: containerBlob.id,
                error,
            });
            return false;
        }
    }

    async #addNewWidgetToContainer(creationInfo: WidgetCreationInfo, containerId: string): Promise<void> {
        const state = this.store.getState() as RootState;
        const widget = await getWidgetFromCreationInfo(creationInfo, state, this.queryClient);
        const widgetType = widget.type;
        const widgetDefinition = getWidgetDefinition(widgetType as RegisteredWidgetType);

        if (widgetDefinition.maxInstances !== undefined) {
            const refreshedState = this.store.getState() as RootState;
            const currentCount = getWidgetCountByType(refreshedState, widgetType);
            if (currentCount >= widgetDefinition.maxInstances) {
                if (TRACE) {
                    this.#log.trace('Widget instance limit reached (addNewWidgetToContainer)', {
                        widgetType,
                        targetContainerId: containerId,
                    });
                }
                const existingTab = getFirstTabByWidgetType(refreshedState, widgetType);
                if (existingTab) {
                    this.store.dispatch(updateActiveTab(existingTab.containerId, existingTab.id));
                    this.store.dispatch(setActiveContainer(existingTab.containerId));
                }
                return;
            }
        }

        const t = await this.tProvider();
        const widgetDisplayName = getWidgetDisplayName(t, widget);
        const tabId = uuidv4() as WidgetId;
        const widgetClass = this.selfOwnedWidgetRegistry.get(widgetType);
        const initialPersistedState = widgetClass ? createInitialSelfOwnedState(widgetClass, widget) : undefined;
        const reduxWidget = widgetClass
            ? ({ type: widgetType, channelId: widget.channelId || null } as WidgetTabViewModel['widget'])
            : widget;

        const tabViewModel: WidgetTabViewModel = {
            id: tabId,
            name: widgetDisplayName,
            containerId,
            isActive: true,
            channelId: widget.channelId || null,
            widget: reduxWidget,
        };

        // The ledger entry must exist before self-owned model instantiation so
        // `GetInitialData` can seed the model from the creation payload.
        const windowId: WindowId = this.widgetContainerHost ? windowIdSchema.parse(containerId) : WEB_WINDOW_ID;

        if (widgetClass) {
            this.#ledger.set(tabId, {
                id: tabId,
                type: widgetType,
                location: { type: 'workspace', tabId },
                windowId,
                persistedState: initialPersistedState,
            });
        }

        if (widgetClass) {
            const container = this.#containers.get(containerId as WidgetContainerId);

            try {
                if (!container) {
                    throw new Error(
                        `WorkspaceService.addNewWidgetToContainer: no container '${containerId}' for self-owned widget '${widgetType}'`,
                    );
                }
                await container.addInitialWidget(
                    widgetType,
                    tabId,
                    encodeInitialSelfOwnedState(widgetClass, initialPersistedState),
                );
                this.store.dispatch(addTab(tabViewModel, containerId));
            } catch (error) {
                this.#ledger.delete(tabId);

                // addInitialWidget may register an error stand-in before
                // rethrowing, so roll the entry back regardless of how far
                // registration got; no Redux tab will exist to render it.
                try {
                    await container?.disposeWidget(tabId);
                } catch (disposeError) {
                    this.#log.warn('Failed to roll back self-owned widget after add-tab failure', {
                        containerId,
                        disposeError,
                        tabId,
                        widgetType,
                    });
                }

                throw error;
            }
            return;
        }

        this.store.dispatch(addTab(tabViewModel, containerId));
    }

    get saveState(): ReadonlySignal<SaveState> {
        return this.#orchestration.saveState;
    }

    get loadState(): ReadonlySignal<LoadState> {
        return this.#orchestration.loadState;
    }

    async addWidget(widgetType: WidgetType, options?: { x: number; y: number }): Promise<void> {
        const creationInfo: WidgetCreationInfo = options
            ? { creationType: 'from-widget-default', widgetType, workspaceLocation: options }
            : { creationType: 'from-widget-default', widgetType };
        await this.#orchestrateAddWidget(creationInfo);
    }

    async publishLinkIntent(intent: LinkIntent, options: PublishLinkIntentOptions = {}): Promise<void> {
        const tab = this.store.getState().tab.byId[intent.fromTabId];
        const channelId = tab?.channelId ?? tab?.widget?.channelId ?? null;
        const widgetType = tab?.widget?.type;

        if (!tab || !channelId || !widgetType) {
            return;
        }

        const from: WidgetLinkSource = {
            kind: 'widget',
            tabId: intent.fromTabId,
            widgetType,
        };
        const id = uuidv4();

        let event: LinkEvent;
        switch (intent.type) {
            case 'instrument-selected':
                event = {
                    id,
                    type: intent.type,
                    channelId,
                    from,
                    details: intent.details,
                };
                break;
            case 'account-selected':
                event = {
                    id,
                    type: intent.type,
                    channelId,
                    from,
                    details: intent.details,
                };
                break;
            case 'quantity-selected':
                event = {
                    id,
                    type: intent.type,
                    channelId,
                    from,
                    details: intent.details,
                };
                break;
            case 'order-selected':
                event = {
                    id,
                    type: intent.type,
                    channelId,
                    from,
                    details: intent.details,
                };
                break;
            case 'table-rows-selected':
                event = {
                    id,
                    type: intent.type,
                    channelId,
                    from,
                    details: intent.details,
                };
                break;
            default:
                throw new UnreachableCaseError(intent);
        }

        const bridgedEvent = options.bridgeToLegacy !== false ? structuredClone(event) : null;

        this.#publishLinkEvent(event);

        if (bridgedEvent) {
            this.#dispatchLegacyLinkingAction(bridgedEvent);
        }
    }

    async publishExternalInstrument(instrument: string, integrationId: string): Promise<void> {
        this.#publishLinkEvent({
            id: uuidv4(),
            type: 'instrument-selected',
            channelId: externalAppLinkingChannelId,
            from: { kind: 'external', integrationId },
            details: { instrument },
        });
    }

    subscribeLinkEvents(handler: LinkEventHandler): () => void {
        this.#linkEventHandlers.add(handler);

        return () => {
            this.#linkEventHandlers.delete(handler);
        };
    }

    #publishLinkEvent(event: LinkEvent): void {
        for (const handler of this.#linkEventHandlers) {
            try {
                handler(event);
            } catch (error) {
                this.#log.error('Workspace link event handler failed', {
                    error,
                    eventId: event.id,
                    eventType: event.type,
                });
            }
        }
    }

    #dispatchLegacyLinkingAction(event: LinkEvent): void {
        if (event.from.kind !== 'widget') return;

        try {
            switch (event.type) {
                case 'instrument-selected':
                    this.store.dispatch(
                        workspaceLinkingInstrumentSelected(event.from.tabId, parseInstrument(event.details.instrument)),
                    );
                    break;
                case 'account-selected':
                    this.store.dispatch(workspaceLinkingAccountSelected(event.from.tabId, event.details.accountId));
                    break;
                case 'quantity-selected':
                    this.store.dispatch(workspaceLinkingQuantitySelected(event.from.tabId, event.details.quantity));
                    break;
                case 'order-selected':
                    this.store.dispatch(
                        workspaceLinkingOrderSelected(
                            event.from.tabId,
                            event.details.orderId,
                            event.details.instrument,
                        ),
                    );
                    break;
                // Table peers consume this event directly from the hub.
                case 'table-rows-selected':
                    break;
                default:
                    throw new UnreachableCaseError(event);
            }
        } catch (error) {
            this.#log.error('Failed to bridge workspace link event to legacy Redux linking', {
                error,
                eventId: event.id,
                eventType: event.type,
            });
        }
    }

    #id = new Signal<string | undefined>(undefined);

    get id(): ReadonlySignal<string | undefined> {
        return this.#id;
    }

    async setActiveContainer(containerId: string | undefined): Promise<void> {
        this.store.dispatch(setActiveContainer(containerId));
    }

    async cycleFocusForward(): Promise<void> {
        this.#cycleFocus(1);
    }

    async cycleFocusBackward(): Promise<void> {
        this.#cycleFocus(-1);
    }

    #cycleFocus(direction: 1 | -1) {
        const { mruOrder, activeContainerId } = this.store.getState().container;
        if (mruOrder.length <= 1) return;

        const currentIndex = activeContainerId ? mruOrder.indexOf(activeContainerId) : -1;
        const nextIndex = currentIndex === -1 ? 0 : (currentIndex + direction + mruOrder.length) % mruOrder.length;

        this.store.dispatch(cycleActiveContainer(mruOrder[nextIndex]));
    }

    #switcherSnapshot: string[] | null = null;
    #switcherIndex = 0;
    #isSwitcherActive = new Signal(false);
    #isSwitcherOverlayVisible = new Signal(false);
    #switcherMruSnapshot = new Signal<readonly string[] | null>(null);
    #overlayTimer: ReturnType<typeof setTimeout> | null = null;

    get isSwitcherActive(): ReadonlySignal<boolean> {
        return this.#isSwitcherActive;
    }

    get isSwitcherOverlayVisible(): ReadonlySignal<boolean> {
        return this.#isSwitcherOverlayVisible;
    }

    get switcherMruSnapshot(): ReadonlySignal<readonly string[] | null> {
        return this.#switcherMruSnapshot;
    }

    async handleCycleFocusKeyDown(reverse?: boolean): Promise<void> {
        const { mruOrder } = this.store.getState().container;
        if (mruOrder.length <= 1) return;

        const { activeContainerId } = this.store.getState().container;
        const step = reverse ? -1 : 1;

        if (this.#switcherSnapshot === null) {
            // First press: snapshot MRU
            this.#switcherSnapshot = [...mruOrder];
            this.#switcherMruSnapshot.value = this.#switcherSnapshot;
            // When no widget is focused, start at index 0 (most recent);
            // otherwise skip to index 1 (second most recent, since 0 is already active)
            const hasActiveWidget = activeContainerId != null;
            this.#switcherIndex = reverse ? this.#switcherSnapshot.length - 1 : hasActiveWidget ? 1 : 0;
            this.#isSwitcherActive.value = true;

            this.#overlayTimer = setTimeout(() => {
                this.#isSwitcherOverlayVisible.value = true;
                this.#overlayTimer = null;
            }, OVERLAY_DELAY_MS);
        } else {
            // Subsequent press: advance/retreat through snapshot
            this.#switcherIndex =
                (this.#switcherIndex + step + this.#switcherSnapshot.length) % this.#switcherSnapshot.length;
        }

        this.store.dispatch(cycleActiveContainer(this.#switcherSnapshot[this.#switcherIndex]));
    }

    async handleCycleFocusKeyUp(): Promise<void> {
        if (this.#switcherSnapshot === null) return;

        // Cancel overlay timer if it hasn't fired yet
        if (this.#overlayTimer !== null) {
            clearTimeout(this.#overlayTimer);
            this.#overlayTimer = null;
        }

        // Commit the selection (reorders MRU via setActiveContainer)
        this.store.dispatch(setActiveContainer(this.#switcherSnapshot[this.#switcherIndex]));

        // Clean up switcher state
        this.#switcherSnapshot = null;
        this.#switcherMruSnapshot.value = null;
        this.#switcherIndex = 0;
        this.#isSwitcherActive.value = false;
        this.#isSwitcherOverlayVisible.value = false;
    }

    async clearActiveContainer(): Promise<void> {
        this.store.dispatch(setActiveContainer(undefined));
    }

    #getWidgetModel(tabId: string): object | undefined {
        const tab = this.store.getState().tab.byId[tabId];
        // Self-owned models live with their WidgetContainer, including background
        // tabs and entitlement replacements. Only legacy models self-register.
        const owned = tab && this.getWidgetContainer(tab.containerId)?.getWidget(tabId as WidgetId);
        return owned ?? this.#widgetRegistry.get(tabId);
    }

    registerWidget(tabId: string, model: object): void {
        this.#widgetRegistry.set(tabId, model);
    }

    deregisterWidget(tabId: string): void {
        this.#widgetRegistry.delete(tabId);
    }

    async *handleCommandQuery(request: CommandQueryRequest, signal: AbortSignal): AsyncIterable<CommandQueryResponse> {
        const { query, requestId, subject, mode } = request;
        const t = await this.tProvider();

        const isExplicit = mode === 'explicit' && subject?.type === 'symbol';
        const hasSymbol = subject?.type === 'symbol';

        // Explicit mode: emit symbol-scoped entries only, filtered by query. Otherwise emit the
        // standard workspace add-widget list (in implicit mode we additionally emit symbol-scoped
        // entries below).
        if (!isExplicit) {
            const workspaceEntries = Object.entries(allWidgets)
                .filter(([widgetType, def]) => widgetType === def.type)
                .map(([widgetType, def]) => {
                    const label = `Add ${t(def.nameKey)} Widget`;
                    const score = scoreCommand(query, label);
                    return {
                        type: 'command' as const,
                        id: `workspace:add-${widgetType}`,
                        label,
                        source: 'workspace' as const,
                        score,
                        action: {
                            eventKey: 'workspace::addWidget',
                            payload: { widgetType: widgetType as WidgetType },
                        },
                    };
                })
                .filter((entry) => entry.score > 0);

            if (signal.aborted) return;
            yield { type: 'command-query-response', requestId, entries: workspaceEntries, done: false };
        }

        // Symbol-scoped entries (both implicit and explicit modes)
        if (hasSymbol) {
            const symbol = subject.value;
            const symbolEntries = this.#buildSymbolEntries(symbol);
            const scored = symbolEntries
                .map((entry) => ({
                    ...entry,
                    score: isExplicit ? scoreCommand(query, entry.label) : 1.0,
                }))
                .filter((entry) => entry.score > 0);
            if (signal.aborted) return;
            if (scored.length > 0) {
                yield { type: 'command-query-response', requestId, entries: scored, done: false };
            }
        }

        // Active widget batch
        const { activeContainerId } = this.store.getState().container;
        if (activeContainerId) {
            const container = this.store.getState().container.byId[activeContainerId];
            if (container?.activeTabId) {
                const containerId = activeContainerId;
                const tabId = container.activeTabId;
                if (!isExplicit) {
                    const widgetEntries = [
                        {
                            type: 'command' as const,
                            id: 'widget:close-widget',
                            label: 'Close Widget',
                            source: 'widget' as const,
                            score: scoreCommand(query, 'Close Widget'),
                            action: { eventKey: 'workspace::removeWidget', payload: { tabId } },
                        },
                        {
                            type: 'command' as const,
                            id: 'widget:toggle-maximize',
                            label: 'Toggle Maximize',
                            source: 'widget' as const,
                            score: scoreCommand(query, 'Toggle Maximize'),
                            action: { eventKey: 'workspace::toggleMaximize', payload: { containerId } },
                        },
                    ].filter((entry) => entry.score > 0);

                    if (signal.aborted) return;
                    if (widgetEntries.length > 0) {
                        yield { type: 'command-query-response', requestId, entries: widgetEntries, done: false };
                    }
                }

                // Focused-widget symbol delegation: "Set ${symbol} on ${widgetName}".
                if (hasSymbol) {
                    const model = this.#getWidgetModel(container.activeTabId);
                    const symbol = subject.value;
                    const delegated = this.#buildFocusedWidgetSymbolEntry(model, symbol, query, isExplicit);
                    if (delegated) {
                        if (signal.aborted) return;
                        yield { type: 'command-query-response', requestId, entries: [delegated], done: false };
                    }
                }

                // Delegate to widget model if it implements CommandHandler (non-explicit only)
                if (!isExplicit) {
                    const model = this.#getWidgetModel(container.activeTabId);
                    if (
                        model &&
                        'handleCommandQuery' in model &&
                        typeof (model as CommandHandler).handleCommandQuery === 'function'
                    ) {
                        for await (const response of (model as CommandHandler).handleCommandQuery(request, signal)) {
                            if (signal.aborted) return;
                            yield response;
                        }
                    }
                }
            }
        }

        yield { type: 'command-query-response', requestId, entries: [], done: true };
    }

    #buildSymbolEntries(symbol: string): CommandEntry[] {
        const entries: CommandEntry[] = [
            {
                type: 'command',
                id: `symbol:open-alpha-eye:${symbol}`,
                label: `Open ${symbol} in AlphaEye`,
                source: 'workspace',
                score: 1.0,
                action: {
                    eventKey: 'workspace::openSymbolInWidget',
                    payload: { widgetType: 'alpha-eye' as WidgetType, instrument: symbol },
                },
            },
            {
                type: 'command',
                id: `symbol:open-news:${symbol}`,
                label: `Open ${symbol} in News`,
                source: 'workspace',
                score: 1.0,
                action: {
                    eventKey: 'workspace::openSymbolInWidget',
                    payload: { widgetType: 'news' as WidgetType, instrument: symbol },
                },
            },
            {
                type: 'command',
                id: `symbol:open-chart:${symbol}`,
                label: `Open ${symbol} in Chart`,
                source: 'workspace',
                score: 1.0,
                action: {
                    eventKey: 'workspace::openSymbolInWidget',
                    payload: { widgetType: 'tradingview-chart' as WidgetType, instrument: symbol },
                },
            },
        ];

        // Per-channel entries: one per color that has ≥1 widget assigned in the current workspace.
        const tabsById = (this.store.getState() as { tab?: { byId: Record<string, { channelId: string | null }> } }).tab
            ?.byId;
        const usedChannels = new Set<string>();
        if (tabsById) {
            for (const tab of Object.values(tabsById)) {
                if (tab.channelId) usedChannels.add(tab.channelId);
            }
        }

        for (const color of channelColors) {
            if (!color) continue;
            if (!usedChannels.has(color)) continue;
            const displayName =
                APP_ELECTRON && color === externalAppLinkingChannelId
                    ? externalAppLinkingLabel
                    : color[0].toUpperCase() + color.slice(1);
            entries.push({
                type: 'command',
                id: `symbol:set-channel:${color}:${symbol}`,
                label: `Set ${symbol} on ${displayName} Channel`,
                source: 'workspace',
                score: 1.0,
                action: {
                    eventKey: 'workspace::setChannelSymbol',
                    payload: { channel: color, instrument: symbol },
                },
            });
        }

        return entries;
    }

    #buildFocusedWidgetSymbolEntry(
        model: object | undefined,
        symbol: string,
        query: string,
        isExplicit: boolean,
    ): CommandEntry | null {
        if (!model || !('eventBus' in model)) return null;
        const eventBus = (
            model as {
                eventBus: {
                    hasEvent(key: string): boolean;
                    getSignal?: (key: string) => { value: string } | undefined;
                };
            }
        ).eventBus;
        if (!eventBus.hasEvent('widget::instrumentChange')) return null;
        const nameSignal = eventBus.getSignal?.('widget::name');
        const widgetName = nameSignal?.value ?? 'Widget';
        const label = `Set ${symbol} on ${widgetName}`;
        const score = isExplicit ? scoreCommand(query, label) : 1.0;
        if (score <= 0) return null;
        return {
            type: 'command',
            id: `symbol:set-focused:${symbol}`,
            label,
            source: 'widget',
            score,
            action: { eventKey: 'widget::instrumentChange', payload: { instrument: symbol } },
        };
    }

    executeCommand(action: CommandAction): void {
        switch (action.eventKey) {
            case 'workspace::addWidget':
                void this.addWidget((action.payload as { widgetType: WidgetType }).widgetType);
                break;
            case 'workspace::cycleFocus': {
                const { reverse } = (action.payload as { reverse?: boolean } | null) ?? {};
                void this.handleCycleFocusKeyDown(reverse);
                break;
            }
            case 'workspace::clearActiveContainer':
                void this.clearActiveContainer();
                break;
            case 'workspace::removeWidget': {
                const { tabId, containerId } = action.payload as { tabId?: string; containerId?: string };
                if (tabId) {
                    void this.removeWidget(tabId);
                } else if (containerId) {
                    void this.removeContainer(containerId);
                }
                break;
            }
            case 'workspace::toggleMaximize': {
                const { containerId } = action.payload as { containerId: string };
                const isMaximized = getMaximizedContainerId(this.store.getState()) === containerId;
                this.store.dispatch(setMaximizedContainer(isMaximized ? undefined : containerId));
                break;
            }
            case 'workspace::openSymbolInWidget': {
                const { widgetType, instrument } = action.payload as { widgetType: WidgetType; instrument: string };
                this.#openSymbolInWidget(widgetType, instrument);
                break;
            }
            case 'workspace::setChannelSymbol': {
                const { channel, instrument } = action.payload as { channel: string; instrument: string };
                this.#setChannelSymbol(channel, instrument);
                break;
            }
            default:
                this.#dispatchToActiveWidget(action);
                break;
        }
    }

    #openSymbolInWidget(widgetType: WidgetType, instrument: string): void {
        if (widgetType === 'alpha-eye' || widgetType === 'alpha-lens') {
            this.store.dispatch(createAlphaEyeFromSymbol({ defaultSymbol: instrument, channelId: null }));
            return;
        }
        if (widgetType === 'tradingview-chart') {
            this.store.dispatch(createChartFromSymbol({ defaultSymbol: instrument, channelId: null }));
            return;
        }
        if (widgetType === 'news') {
            this.store.dispatch(createNewsFromSymbol({ defaultSymbol: instrument, channelId: null }));
            return;
        }
        // Fallback: add the widget of the given type. The new widget's own default symbol
        // handling applies; further symbol delivery would require post-mount routing.
        void this.addWidget(widgetType);
    }

    #setChannelSymbol(channel: string, instrument: string): void {
        const state = this.store.getState() as {
            tab?: { byId: Record<string, { id: string; channelId: string | null }> };
        };
        const tabsById = state.tab?.byId;
        if (!tabsById) return;
        for (const tab of Object.values(tabsById)) {
            if (tab.channelId !== channel) continue;
            const model = this.#getWidgetModel(tab.id);
            if (!model || !('eventBus' in model)) continue;
            const eventBus = (
                model as { eventBus: { hasEvent(key: string): boolean; emit(key: string, payload: unknown): void } }
            ).eventBus;
            if (eventBus.hasEvent('widget::instrumentChange')) {
                eventBus.emit('widget::instrumentChange', { instrument });
            }
        }
    }

    #dispatchToActiveWidget(action: CommandAction): void {
        const { activeContainerId } = this.store.getState().container;
        if (!activeContainerId) return;

        const container = this.store.getState().container.byId[activeContainerId];
        if (!container?.activeTabId) return;

        const model = this.#getWidgetModel(container.activeTabId);
        if (!model) return;

        if ('eventBus' in model) {
            const eventBus = (
                model as { eventBus: { hasEvent(key: string): boolean; emit(key: string, payload: unknown): void } }
            ).eventBus;
            if (eventBus.hasEvent(action.eventKey)) {
                eventBus.emit(action.eventKey, action.payload);
            }
        }
    }

    async updateSettings(settings: WorkspaceSettingsUpdate): Promise<void> {
        this.#throwIfSaveInProgress();

        const currentWorkspace = getAssertedCurrentWorkspace(this.store.getState());
        const wasAlreadyMosaic = currentWorkspace.layoutSettings?.enableMosaicLayout ?? false;
        const willBeMosaic = settings.enableMosaicLayout;
        let layoutUpdates: LayoutUpdate[] = [];

        if (!APP_ELECTRON && !this.widgetContainerHost && !wasAlreadyMosaic && willBeMosaic) {
            layoutUpdates = await this.#getMosaicLayoutUpdates(currentWorkspace);
        }

        this.#throwIfSaveInProgress();

        if (layoutUpdates.length > 0) {
            this.store.dispatch(updateMultipleWorkspaceLayouts(layoutUpdates));
        }
        this.store.dispatch(updateWorkspaceSettings(settings));
        await this.save({
            saveType: 'save',
            name: settings.name,
            promoteToDefault: settings.isPrimaryWorkspace,
        });
    }

    #throwIfSaveInProgress(): void {
        if (this.saveState.value !== 'saving') return;

        this.store.dispatch(
            setSnackbar(t('views::dashboard::workspace::save-in-progress', 'Save already in progress'), {
                variant: 'info',
                key: 'workspace-save-in-progress',
            }),
        );
        throw new WorkspaceSaveInProgressError();
    }

    async #getMosaicLayoutUpdates(currentWorkspace: WorkspaceViewModel): Promise<LayoutUpdate[]> {
        const containerLayouts = (this.store.getState() as RootState).layout.byContainerId;
        const legacyLayouts: Record<string, NormalizedLayout> = {};

        for (const containerId of currentWorkspace.containerIds) {
            const layout = containerLayouts[containerId];
            if (!layout) continue;
            legacyLayouts[containerId] = {
                x: layout.x,
                y: layout.y,
                w: layout.w,
                h: layout.h,
            };
        }

        const containerCount = Object.keys(legacyLayouts).length;

        if (containerCount === 0) {
            this.#log.info({
                message: 'No containers to convert',
                workspaceId: currentWorkspace.id,
                workspaceName: currentWorkspace.name,
            });
            return [];
        }

        const { convertLegacyToMosaic } = await import('src/routes/dashboard/svelte-layout/mosaic-legacy-conversion');

        const convertedLayouts = convertLegacyToMosaic(legacyLayouts);
        const updates: LayoutUpdate[] = Object.entries(convertedLayouts).map(([containerId, layout]) => ({
            containerId,
            layout: {
                x: layout.x,
                y: layout.y,
                w: layout.w,
                h: layout.h,
            },
        }));

        if (TRACE) {
            this.#log.trace({
                message: 'Converted workspace layouts to mosaic',
                workspaceId: currentWorkspace.id,
                workspaceName: currentWorkspace.name,
                totalContainers: containerCount,
                beforeLayouts: legacyLayouts,
                afterLayouts: convertedLayouts,
            });
        }

        return updates;
    }

    async save({
        saveType,
        name: providedName,
        promoteToDefault: providedPromoteToDefault,
    }: SaveWorkspaceParams): Promise<void> {
        const attempt = this.#orchestration.beginSave();
        if (attempt === null) {
            this.#throwIfSaveInProgress();
            throw new WorkspaceSaveInProgressError();
        }

        try {
            this.store.dispatch(setSavingTrue());

            const currentWorkspace = getAssertedCurrentWorkspace(this.store.getState());

            const isFirstSave = currentWorkspace.isUnsaved;
            const mustMakeNew =
                isFirstSave || !doesAccessGrantAllowRequest(currentWorkspace.permissions.effectiveAccess, 'writer');

            let name = currentWorkspace.name;
            const promoteToDefault = providedPromoteToDefault ?? false;

            if (saveType === 'saveAs' || isFirstSave) {
                if (providedName === undefined) {
                    this.#log.error('save() called for first-save/saveAs without a name');
                    this.store.dispatch(savingCancelled());
                    return;
                }
                name = providedName;
            }

            // Iterate in workspace order; Map insertion order is unstable across loads.
            const containerJsons: PersistedWidgetContainer[] = [];
            for (const containerId of currentWorkspace.containerIds) {
                const container = this.#containers.get(containerId as WidgetContainerId);
                if (!container) {
                    throw new Error(`Cannot save workspace while WidgetContainer '${containerId}' is unavailable`);
                }
                const json = await container.toJSON();
                const encodedTabs = json.tabs.map((tab) => {
                    const tabId = tab.id as WidgetId;
                    const widgetClass = this.selfOwnedWidgetRegistry.get(tab.widget.type);
                    if (!widgetClass) return tab;

                    let persistedState: unknown;
                    let wireState: Record<string, unknown>;
                    try {
                        persistedState = decodeSelfOwnedWidgetState(widgetClass.stateSchema, tab.widget);
                        wireState = encodeSelfOwnedWidgetState(widgetClass.stateSchema, persistedState) as Record<
                            string,
                            unknown
                        >;
                    } catch (error) {
                        // Stand-ins carry persisted-but-invalid state verbatim
                        // (the very reason an ErrorWidgetModel exists), and the
                        // container already embedded that raw payload in
                        // tab.widget. Save it as-is rather than aborting the
                        // whole workspace save; the ledger keeps its last-known
                        // state.
                        this.#log.warn(`Saving widget '${tab.widget.type}' state verbatim: schema encoding failed`, {
                            tabId,
                            widgetType: tab.widget.type,
                            error,
                        });
                        return tab;
                    }
                    const ledgerEntry = this.#ledger.get(tabId);
                    if (ledgerEntry) {
                        this.#ledger.set(tabId, { ...ledgerEntry, persistedState });
                    }

                    return {
                        ...tab,
                        widget: { ...tab.widget, ...wireState } as WidgetTab['widget'],
                    };
                });

                containerJsons.push({ ...json, tabs: encodedTabs });
            }

            const { localContainers, remoteContainers } = partitionContainersByLocality(containerJsons);

            this.localWidgetStorageService.saveLocalWidgets(currentWorkspace.id, localContainers);

            const unNormalizedWorkspace = createWorkspaceFromContainers(currentWorkspace, remoteContainers);

            const isDefaultWorkspace = currentWorkspace.isDefaultWorkspace;
            let newId = '';

            if (mustMakeNew || saveType === 'saveAs') {
                const asNewWorkspace: NewWorkspace = {
                    ...unNormalizedWorkspace,
                    name,
                    ...(isFirstSave
                        ? {
                              layoutSettings: {
                                  ...unNormalizedWorkspace.layoutSettings,
                                  enableMosaicLayout: true,
                              },
                          }
                        : { originatedFrom: currentWorkspace.id }),
                };
                const fullyNewWorkspace = await this.#createWorkspaceMutation.mutate({ workspace: asNewWorkspace });

                this.suggestionsService.addRecentlyAccessed({ type: 'workspace', id: fullyNewWorkspace.id });

                this.localWidgetStorageService.saveLocalWidgets(fullyNewWorkspace.id, localContainers);

                if (isDefaultWorkspace || promoteToDefault) {
                    await changeDefaultWorkspaceOnAPI(fullyNewWorkspace.id);
                }

                try {
                    const analyticsService = staticContainer.getOptional('AnalyticsService');
                    analyticsService?.track({
                        type: 'workspace_created',
                        workspaceId: fullyNewWorkspace.id,
                        workspaceName: fullyNewWorkspace.name,
                        isPrimary: isDefaultWorkspace || promoteToDefault,
                    });
                } catch (analyticsError) {
                    this.#log.warn('Failed to track workspace_created', { error: analyticsError });
                }

                if (!isDefaultWorkspace) {
                    newId = fullyNewWorkspace.id;
                }
            } else {
                await this.#updateWorkspaceMutation.mutate({ workspace: unNormalizedWorkspace });
                if (promoteToDefault && !isDefaultWorkspace) {
                    await changeDefaultWorkspaceOnAPI(currentWorkspace.id);
                }
            }

            await Promise.all([
                this.queryClient.invalidateQueries({ queryKey: ['workspace'] }),
                this.queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
            ]);

            for (const container of this.#containers.values()) {
                await container.markCleanAll();
            }

            this.store.dispatch(savingCompleted());

            // Keep the success path independent from the post-save reload.
            const analyticsService = staticContainer.getOptional('AnalyticsService');
            const widgetCount = containerJsons.reduce((sum, c) => sum + c.tabs.length, 0);

            analyticsService?.track({
                type: 'dashboard_saved',
                dashboardId: currentWorkspace.id,
                dashboardName: currentWorkspace.name,
                widgetCount,
            });

            this.store.dispatch(
                setSnackbar(
                    saveType === 'save'
                        ? t('views::dashboard::workspace::save-success', 'Workspace saved')
                        : t('views::dashboard::workspace::create-success', 'Workspace created'),
                    {
                        variant: 'success',
                    },
                ),
            );

            if (newId) {
                const slug = generateSlug(name);
                if (this.navigationService) {
                    await this.#navigate(`/dashboard/${newId}/${slug}`);
                } else {
                    await this.load(newId);
                }
            } else {
                // TODO(PLAT-12166): this re-load is a carryover from the saga era. What we
                // actually want post-save is to pick up any server-normalized
                // fields, not a full reload. It also interacts poorly with an
                // in-flight load (this supersedes it via the attempt counter,
                // which we didn't do pre-migration). Watching for fallout.
                const currentPathname = this.#routeLocation()?.pathname;
                const workspaceIdFromUrl =
                    currentPathname && isRoutePath(currentPathname, '/dashboard')
                        ? (currentPathname.split('/')[2] ?? 'default')
                        : currentWorkspace.id;
                try {
                    await this.load(workspaceIdFromUrl || 'default');
                } catch (reloadError) {
                    this.#log.warn('Post-save workspace reload failed', { error: reloadError });
                }
            }
        } catch (e) {
            this.store.dispatch(
                setSnackbar(t('views::dashboard::workspace::save-error', 'Could not save workspace'), {
                    variant: 'error',
                }),
            );
            this.store.dispatch(workspaceSavingFailed());

            if (e instanceof Error) {
                this.#log.error({ message: 'Saving workspace failed', error: e });
            } else {
                this.#log.fatal({ message: 'unknown exception', details: e });
            }
            throw e;
        } finally {
            this.#orchestration.endSave(attempt);
        }
    }

    async load(workspaceId: string, options: WorkspaceLoadOptions = {}): Promise<void> {
        const currentWorkspace = getCurrentWorkspace(this.store.getState() as RootState);
        const targetsCurrentWorkspace =
            currentWorkspace !== null &&
            (workspaceId === currentWorkspace.id || (workspaceId === 'default' && currentWorkspace.isDefaultWorkspace));
        if (targetsCurrentWorkspace && currentWorkspace.isDirty && options.reason !== 'permission-context-change') {
            this.#log.info(`Preserving dirty workspace '${currentWorkspace.id}' instead of reloading it`, {
                reason: options.reason ?? 'navigation',
                workspaceId: currentWorkspace.id,
            });
            return;
        }

        const attempt = this.#beginLoad();

        try {
            if (options.reason === 'permission-context-change') {
                this.#log.info(`Reloading workspace '${workspaceId}' after permission context change`, {
                    workspaceId,
                });
                await Promise.all([
                    this.queryClient.invalidateQueries({ queryKey: ['workspace'], refetchType: 'none' }),
                    this.queryClient.invalidateQueries({ queryKey: ['workspaces'], refetchType: 'none' }),
                ]);
                if (this.#isStaleLoad(attempt)) return;
            }

            if (workspaceId === 'new') {
                try {
                    await this.#loadUnsavedWorkspace(attempt);
                } catch (e) {
                    if (this.#isStaleLoad(attempt)) return;
                    this.#log.warn('Unable to initialize unsaved workspace', { error: e });
                    this.store.dispatch(clearWorkspaceState());
                    this.store.dispatch(setWorkspaceError('generic'));
                }
                return;
            }

            this.store.dispatch(setResearchingWorkspace());
            prefetchWorkspaceLoadDependencies(this.queryClient);

            try {
                const { workspaceViewModel, cleanedWorkspace } = await this.queryClient.fetchQuery({
                    queryFn: () =>
                        getWorkspaceViewModelSetup(workspaceId, this.queryClient, this.localWidgetStorageService),
                    queryKey: ['workspace', 'active', workspaceId],
                });

                if (this.#isStaleLoad(attempt)) return;

                const cleanedContainers = cleanedWorkspace.containers;
                const containerViewModels = cleanedContainers.map((c) => createContainerViewModel(c));
                const layoutViewModels = cleanedContainers.map((c) => createContainerLayoutViewModel(c.layout, c.id));
                const widgetTabViewModels = await generateWidgetTabViewModels(cleanedWorkspace, this.queryClient);

                if (this.#isStaleLoad(attempt)) return;

                const currentWorkspaceVm = getCurrentWorkspace(this.store.getState() as RootState);

                if (currentWorkspaceVm === workspaceViewModel) {
                    this.#seedSelfOwnedLedgerFromBlob(cleanedContainers);
                    if (this.widgetContainerHost) {
                        await this.#syncHostedContainersFromBlob(cleanedContainers, attempt);
                    } else {
                        await this.#instantiateSelfOwnedWidgetsAfterDispatch(cleanedContainers);
                    }
                    if (this.#isStaleLoad(attempt)) return;
                    this.store.dispatch(completeWorkspaceResearch());
                    return;
                }

                const isWorkspaceSwitch =
                    currentWorkspaceVm !== null && currentWorkspaceVm.id !== workspaceViewModel.id;

                if (isWorkspaceSwitch) {
                    try {
                        const analyticsService = staticContainer.getOptional('AnalyticsService');
                        analyticsService?.track({
                            type: 'workspace_switched',
                            fromWorkspaceId: currentWorkspaceVm!.id,
                            toWorkspaceId: workspaceViewModel.id,
                            toWorkspaceName: workspaceViewModel.name,
                        });
                    } catch (analyticsError) {
                        this.#log.warn('Failed to track workspace_switched', { error: analyticsError });
                    }

                    const prevMosaicTemplate = currentWorkspaceVm?.mosaicTemplate;

                    if (this.widgetContainerHost) {
                        await this.#closeTrackedContainers('workspace-replaced');
                        if (this.#isStaleLoad(attempt)) return;
                    }

                    this.store.dispatch(clearWorkspaceState());

                    this.#seedSelfOwnedLedgerFromBlob(cleanedContainers);

                    this.store.dispatch(
                        initializeNormalizedData(
                            workspaceViewModel,
                            containerViewModels,
                            layoutViewModels,
                            widgetTabViewModels,
                        ),
                    );

                    if (this.widgetContainerHost) {
                        await this.#syncHostedContainersFromBlob(cleanedContainers, attempt);
                    } else {
                        await this.#instantiateSelfOwnedWidgetsAfterDispatch(cleanedContainers);
                    }
                    if (this.#isStaleLoad(attempt)) return;

                    // When saving an unsaved mosaic-template workspace for the first time,
                    // the navigation to the new workspace ID triggers a full reload which
                    // loses the ephemeral mosaicTemplate. Restore it so template slots
                    // remain visible after the initial save.
                    if (prevMosaicTemplate && currentWorkspaceVm?.id === 'new') {
                        this.store.dispatch(setMosaicTemplate(prevMosaicTemplate));
                    }
                } else {
                    const wholeStore = this.store.getState() as RootState;
                    const existingContainers = wholeStore.container.byId;
                    const existingLayouts = wholeStore.layout.byContainerId;
                    const existingTabs = wholeStore.tab.byId;

                    const newOrUpdatedContainers = containerViewModels.filter(
                        (container) => !isEqual(existingContainers[container.id], container),
                    );

                    const newOrUpdatedLayouts = layoutViewModels.filter(
                        (layout) => !isEqual(existingLayouts[layout.containerId], layout),
                    );

                    const newOrUpdatedTabs = widgetTabViewModels.filter((tab) => !isEqual(existingTabs[tab.id], tab));

                    const newWorkspaceVm = isEqual(workspaceViewModel, currentWorkspaceVm)
                        ? currentWorkspaceVm!
                        : workspaceViewModel;

                    this.#seedSelfOwnedLedgerFromBlob(cleanedContainers);

                    this.store.dispatch(
                        initializeNormalizedData(
                            newWorkspaceVm,
                            newOrUpdatedContainers,
                            newOrUpdatedLayouts,
                            newOrUpdatedTabs,
                        ),
                    );

                    if (this.widgetContainerHost) {
                        await this.#syncHostedContainersFromBlob(cleanedContainers, attempt);
                    } else {
                        await this.#instantiateSelfOwnedWidgetsAfterDispatch(cleanedContainers);
                    }
                    if (this.#isStaleLoad(attempt)) return;
                }

                try {
                    const analyticsService = staticContainer.getOptional('AnalyticsService');

                    if (analyticsService) {
                        analyticsService.track({
                            type: 'dashboard_opened',
                            dashboardId: workspaceViewModel.id,
                            dashboardName: workspaceViewModel.name,
                            isDefault: workspaceViewModel.isDefaultWorkspace,
                        });

                        for (const tab of widgetTabViewModels) {
                            analyticsService.track({
                                type: 'widget_loaded',
                                dashboardId: workspaceViewModel.id,
                                widgetType: tab.widget.type,
                                widgetId: tab.id,
                            });
                        }
                    }
                } catch (analyticsError) {
                    this.#log.warn('Failed to track analytics events', { error: analyticsError });
                }
            } catch (e) {
                if (this.#isStaleLoad(attempt)) return;

                const pathname = this.#routeLocation()?.pathname;
                if (e instanceof WorkspaceNotFoundError && workspaceId === 'default') {
                    if (this.navigationService) {
                        await this.#navigate('/dashboard/new');
                    } else {
                        await this.load('new');
                    }
                } else if (e instanceof WorkspacePermissionError) {
                    this.store.dispatch(clearWorkspaceState());
                    this.store.dispatch(setWorkspaceError('permission'));
                    return;
                } else {
                    this.#log.warn('Unable to sync current workspace', { error: e, pathname });
                    this.store.dispatch(clearWorkspaceState());
                    this.store.dispatch(setWorkspaceError('generic'));
                    return;
                }
            }

            if (this.#isStaleLoad(attempt)) return;
            this.store.dispatch(completeWorkspaceResearch());
        } finally {
            this.#endLoad(attempt);
        }
    }

    async #loadUnsavedWorkspace(attempt: WorkspaceLoadAttempt): Promise<void> {
        const state = this.store.getState() as RootState;
        const config: CreateFromTemplateLibraryAction | null = state.workspace.pendingTemplateConfig;

        if (config) {
            if (config.template.kind === 'container') {
                const cleanedWorkspace = stripUnrecognizedWidgets(config.template.workspace);

                const containerViewModels = cleanedWorkspace.containers.map((c) => createContainerViewModel(c));
                const layoutViewModels = cleanedWorkspace.containers.map((c) =>
                    createContainerLayoutViewModel(c.layout, c.id),
                );
                const widgetTabViewModels = await generateWidgetTabViewModels(cleanedWorkspace, this.queryClient);

                // A newer load() superseded us while generating widget tabs. Bail
                // before consuming pendingTemplateConfig so the superseding attempt
                // can still see and apply it.
                if (this.#isStaleLoad(attempt)) return;

                this.store.dispatch(clearPendingTemplateConfig());
                if (this.widgetContainerHost) {
                    await this.#closeTrackedContainers('workspace-replaced');
                    if (this.#isStaleLoad(attempt)) return;
                }
                this.store.dispatch(clearWorkspaceState());

                const workspaceName = config.name || 'Untitled Workspace';
                const workspaceVm: WorkspaceViewModel = {
                    ...UNSAVED_WORKSPACE,
                    name: workspaceName,
                    layoutSettings: {
                        containerLayoutLocked: false,
                        gridSnappingEnabled: config.layoutSettings.gridSnappingEnabled,
                        enableMosaicLayout: config.layoutSettings.enableMosaicLayout,
                        mosaicGutterPx: config.layoutSettings.mosaicGutterPx,
                    },
                    containerIds: containerViewModels.map((c) => c.id),
                };

                this.#seedSelfOwnedLedgerFromBlob(cleanedWorkspace.containers);

                this.store.dispatch(
                    initializeNormalizedData(workspaceVm, containerViewModels, layoutViewModels, widgetTabViewModels),
                );

                if (this.widgetContainerHost) {
                    await this.#syncHostedContainersFromBlob(cleanedWorkspace.containers, attempt);
                } else {
                    await this.#instantiateSelfOwnedWidgetsAfterDispatch(cleanedWorkspace.containers);
                }
                if (this.#isStaleLoad(attempt)) return;
            } else if (config.template.kind === 'mosaic') {
                this.store.dispatch(clearPendingTemplateConfig());
                if (this.widgetContainerHost) {
                    await this.#closeTrackedContainers('workspace-replaced');
                    if (this.#isStaleLoad(attempt)) return;
                }
                this.store.dispatch(clearWorkspaceState());

                const mosaicTemplate: WorkspaceViewModel['mosaicTemplate'] = {
                    slots: config.template.mosaicTemplate.slots.map((s) => ({ id: s.id, rect: s.rect })),
                    occupiedSlotIds: [],
                    slotContainerMap: {},
                };
                const workspaceName = config.name || 'Untitled Workspace';
                this.store.dispatch(
                    initializeUnsavedWorkspace(true, mosaicTemplate, {
                        name: workspaceName,
                        gridSnappingEnabled: config.layoutSettings.gridSnappingEnabled,
                        mosaicGutterPx: config.layoutSettings.mosaicGutterPx,
                    }),
                );
            } else {
                this.store.dispatch(clearPendingTemplateConfig());
                if (this.widgetContainerHost) {
                    await this.#closeTrackedContainers('workspace-replaced');
                    if (this.#isStaleLoad(attempt)) return;
                }
                this.store.dispatch(clearWorkspaceState());

                const workspaceName = config.name || 'Untitled Workspace';
                this.store.dispatch(
                    initializeUnsavedWorkspace(config.layoutSettings.enableMosaicLayout, undefined, {
                        name: workspaceName,
                        gridSnappingEnabled: config.layoutSettings.gridSnappingEnabled,
                        mosaicGutterPx: config.layoutSettings.mosaicGutterPx,
                    }),
                );
            }

            this.store.dispatch(completeWorkspaceResearch());
            return;
        }

        // Default: no pending config — backwards-compat with query param or plain /dashboard/new.
        const routerSearch = this.#routeLocation()?.search;
        const searchParams = new URLSearchParams(routerSearch || '');
        const templateId = searchParams.get('template');

        let mosaicTemplate: WorkspaceViewModel['mosaicTemplate'] | undefined;
        if (templateId) {
            const template = getMosaicTemplateById(templateId);
            if (template) {
                mosaicTemplate = {
                    slots: template.slots.map((s) => ({ id: s.id, rect: s.rect })),
                    occupiedSlotIds: [],
                    slotContainerMap: {},
                };
            }
        }

        // Clear stale containers/layouts from the previous workspace so the
        // mosaic layout engine (ghost button, free-space detection) doesn't see
        // old data.
        if (this.widgetContainerHost) {
            await this.#closeTrackedContainers('workspace-replaced');
            if (this.#isStaleLoad(attempt)) return;
        }
        this.store.dispatch(clearWorkspaceState());
        this.store.dispatch(initializeUnsavedWorkspace(true, mosaicTemplate));
        this.store.dispatch(completeWorkspaceResearch());
    }

    [Symbol.dispose](): void {
        this.subscription?.();
        this.containerLedgerSubscription?.();
        this.#linkEventHandlers.clear();
        this.#cancelActiveLoad();
        void this.#closeTrackedContainers('service-disposed').catch((error) => {
            this.#log.warn('Failed to close workspace containers during service disposal', { error });
        });
        this.#activeLoadAttempt[Symbol.dispose]();
        this.#orchestration[Symbol.dispose]();
        if (this.#overlayTimer !== null) {
            clearTimeout(this.#overlayTimer);
        }
    }
}
